import type { FormikErrors } from "formik";
import { ApiError } from "@/lib/api";

/**
 * Bridges the Django API's validation-error shape onto Formik field errors.
 *
 * The backend envelopes a failed request as
 * `{ success: false, message: "Validation failed", errors: { field: [msg] } }`
 * (plain DRF views put `{ field: [msg] }` at the top level). Showing only the
 * envelope `message` in a toast hides *which* field failed and *why*, so this
 * maps each server field error back onto its form field — the same channel
 * client-side Yup errors use — and returns any form-level messages for a toast.
 */

/** snake_case → camelCase: `date_of_birth` → `dateOfBirth`. */
function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, c: string) => (c as string).toUpperCase());
}

/** DRF keys that aren't real form fields — surfaced via toast, not on a field. */
const NON_FIELD_KEYS = new Set(["non_field_errors", "detail"]);

/** First human-readable message from DRF's `["msg", ...]` (or a bare string). */
function firstMessage(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return null;
}

/**
 * Pulls the serializer field-error map out of an `ApiError`. Reads the envelope's
 * `errors` object when present, otherwise treats the body itself as the map.
 * Returns `{}` for anything that isn't a structured validation failure.
 */
function extractFieldErrors(err: unknown): Record<string, unknown> {
  if (!(err instanceof ApiError) || !err.data || typeof err.data !== "object") return {};
  const body = err.data as Record<string, unknown>;
  if (body.errors && typeof body.errors === "object") return body.errors as Record<string, unknown>;
  return body;
}

export interface MappedApiErrors {
  /** Formik-shaped errors keyed by form field name (camelCase). */
  fieldErrors: FormikErrors<Record<string, string>>;
  /** True when at least one message was bound to a form field. */
  hadFieldErrors: boolean;
  /** Form-level message not tied to a field (`non_field_errors` / `detail`), if any. */
  formMessage: string | null;
}

/**
 * Maps a rejected-request error onto Formik field errors. snake_case keys are
 * converted to camelCase to match form field names; pass `fieldMap` to override
 * any key whose form field differs (e.g. `{ ssn_tin: "taxId" }`).
 */
export function mapApiErrors(err: unknown, fieldMap: Record<string, string> = {}): MappedApiErrors {
  const raw = extractFieldErrors(err);
  const fieldErrors: Record<string, string> = {};
  let formMessage: string | null = null;

  for (const [key, value] of Object.entries(raw)) {
    const message = firstMessage(value);
    if (!message) continue;
    if (NON_FIELD_KEYS.has(key)) {
      formMessage ??= message;
      continue;
    }
    fieldErrors[fieldMap[key] ?? snakeToCamel(key)] = message;
  }

  return { fieldErrors, hadFieldErrors: Object.keys(fieldErrors).length > 0, formMessage };
}

/**
 * Applies a rejected-request error to a Formik form: binds server field errors to
 * their fields (they render immediately since submit marks every field touched)
 * and returns the best single message for a toast. Use in a form's `catch`:
 *
 * ```ts
 * catch (err) {
 *   toast.error(applyApiErrors(err, { setErrors }, { fieldMap, fallback: "Couldn't save." }));
 * }
 * ```
 */
export function applyApiErrors(
  err: unknown,
  helpers: { setErrors: (errors: FormikErrors<never>) => void },
  opts: { fieldMap?: Record<string, string>; fallback?: string } = {},
): string {
  const { fieldErrors, hadFieldErrors, formMessage } = mapApiErrors(err, opts.fieldMap);
  if (hadFieldErrors) helpers.setErrors(fieldErrors as FormikErrors<never>);

  if (formMessage) return formMessage;
  if (hadFieldErrors) return "Please fix the highlighted fields below.";
  if (err instanceof ApiError) return err.message;
  return opts.fallback ?? "Something went wrong. Please try again.";
}
